import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  InternalAdvanceAccount,
  LendingPaymentSource,
  LendingPaymentTypeInput,
  PlaidEntryPointInput,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams } from 'expo-router';
import useAccountDetails from 'features/accounts/hooks/useAccountDetails';
import {
  formatISODate,
  getExternalDebitCardFromFlow,
} from 'features/advance/helpers/Advance.helpers';
import {
  getInternalAccountAvailableBalance,
  removeDollarPrefix,
} from 'features/advance/helpers/advanceManualRepay.helpers';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyKeyToAmount } from 'shared/utils/numberUtils';
import { formatCurrency } from 'shared/utils/string/currency';

import useAdvancePaymentAccountController from './useAdvancePaymentAccountController/useAdvancePaymentAccountController';

const useRepayAdvanceV2 = () => {
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const [isAmountEntered, setAmountEntered] = useState(false);
  const [transferAmount, setTransferAmount] = useState('0.00');
  const [limitErrorString, setLimitErrorString] = useState<string>();
  const [availableLimitError, setAvailableLimitError] = useState(false);
  const [amountDueError, setAmountDueError] = useState(false);
  const [isNegativeBalance, setNegativeBalanceError] = useState(false);
  const [minimumPaymentError, setMinimumPaymentError] = useState(false);
  const { id, fromDebitCardFlow } = useLocalSearchParams<{
    id: string;
    fromDebitCardFlow?: string;
  }>();

  const {
    accountDetails,
    loading: loading,
    errorModel: advanceError,
  } = useAccountDetails(id);

  const {
    openPlaid: openPlaidLink,
    loading: isPolling,
    paymentOptions: accountOptionsData,
    autoSelectedAccount,
    selectedAccount,
    setSelectedAccount,
    refetch: refetchAutoPaymentOptions,
    error: accountOptionsError,
  } = useAdvancePaymentAccountController({
    paymentType: LendingPaymentTypeInput.Manual,
    plaidEntryPoint: PlaidEntryPointInput.MoveMoney,
  });

  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'manualRepay',
  });

  const activeAdvanceDetails = (accountDetails as InternalAdvanceAccount)
    ?.activeAdvance;
  const amountDue = activeAdvanceDetails?.amountDue?.amount?.toString() ?? '0';
  const amountDueFormatted =
    activeAdvanceDetails?.amountDue?.formatted?.toString() ?? '$0';
  const paymentDueDate = activeAdvanceDetails?.dueDate ?? '';
  const advanceStatus = (accountDetails as InternalAdvanceAccount)
    ?.advanceStatus;

  const isInternalAccountSelected =
    selectedAccount?.source === LendingPaymentSource.InternalDda ||
    selectedAccount?.source === LendingPaymentSource.InternalSavings;

  const isDebitCardSelected =
    selectedAccount?.source === LendingPaymentSource.ExternalDebitCard;

  const minimumDebitCardPaymentAmount = 10;

  // Refetch options after returning from add debit card flow to ensure newly added card appears.
  useEffect(() => {
    if (fromDebitCardFlow === 'true' && refetchAutoPaymentOptions) {
      refetchAutoPaymentOptions();
    }
  }, [fromDebitCardFlow, refetchAutoPaymentOptions]);

  useEffect(() => {
    if (!accountOptionsData) {
      return;
    }

    // If no account is selected, set the auto-selected account.
    if (!selectedAccount && autoSelectedAccount) {
      setSelectedAccount(autoSelectedAccount);
    }

    // Get the external debit card details and set it as the selected account
    // if coming from the add debit card flow.
    const externalDebitCardDetail = getExternalDebitCardFromFlow(
      accountOptionsData,
      fromDebitCardFlow,
    );
    if (externalDebitCardDetail) {
      setSelectedAccount(externalDebitCardDetail);
    }

    setTransferAmount(isAmountEntered ? transferAmount : amountDue);
  }, [
    accountOptionsData,
    amountDue,
    amountDueFormatted,
    autoSelectedAccount,
    fromDebitCardFlow,
    isAmountEntered,
    selectedAccount,
    setSelectedAccount,
    transferAmount,
  ]);

  useEffect(() => {
    const availableBalance =
      getInternalAccountAvailableBalance(selectedAccount);
    const accountBalance = parseFloat(
      removeDollarPrefix(availableBalance ?? '0'),
    );
    const isNegativeBalance = accountBalance < 0;
    const isPaymentAmountBelowMinimum =
      parseFloat(transferAmount) < minimumDebitCardPaymentAmount;
    const availableLimitError = parseFloat(transferAmount) > accountBalance;
    const amountDueError = parseFloat(transferAmount) > parseFloat(amountDue);
    const pendingAmount = parseFloat(amountDue) - parseFloat(transferAmount);
    const dueDate = formatISODate(paymentDueDate, 'MMMM dd, yyyy');
    let errorString: string;

    setAvailableLimitError(false);
    setAmountDueError(false);
    setNegativeBalanceError(false);
    setMinimumPaymentError(false);

    if (availableLimitError && isInternalAccountSelected) {
      setAvailableLimitError(true);
      errorString = translatedText('yourLimit', {
        availableBalance: formatCurrency(availableBalance),
      });
    } else if (amountDueError) {
      setAmountDueError(true);
      errorString = translatedText('limitError', {
        amountDue: formatCurrency(amountDue),
      });
    } else if (isDebitCardSelected && isPaymentAmountBelowMinimum) {
      setMinimumPaymentError(true);
      errorString = translatedText('debitCardMinimumPaymentError', {
        minimumPayment: formatCurrency(minimumDebitCardPaymentAmount),
      });
    } else if (isNegativeBalance && isInternalAccountSelected) {
      setNegativeBalanceError(true);
      errorString = translatedText('yourLimit', {
        availableBalance: formatCurrency(availableBalance),
      });
    } else {
      const formattedPendingAmount = formatCurrency(pendingAmount);

      errorString = translatedText('limitErrorStart', {
        pendingAmount: formattedPendingAmount,
        paymentDueDate: dueDate,
      });
    }
    setLimitErrorString(errorString);
  }, [
    amountDue,
    paymentDueDate,
    transferAmount,
    availableLimitError,
    translatedText,
    isInternalAccountSelected,
    selectedAccount,
    isDebitCardSelected,
  ]);

  const handleNumPadAction = (value: string) => {
    setAmountEntered(true);
    const userEnteredValue = applyKeyToAmount(transferAmount, value);
    setTransferAmount(userEnteredValue ?? transferAmount);
  };

  const openBottomSheet = () => {
    bottomSheetRef?.current?.present();
  };

  // Disable the submit button if:
  // 1. No payment source account is selected
  // 2. User is trying to pay more than the amount due
  // 3. No amount has been entered (transferAmount is still '0.00') - applies to ALL account types
  // 4. User is trying to pay less than the minimum payment amount of $10 - applies to debit cards only
  // 5. For internal accounts (Varo checking/savings) only:
  //    - User is trying to pay more than their available balance, OR
  //    - Account has a negative balance
  // Note: External debit cards and ACH accounts skip balance validation but still require an amount
  const isSubmitDisabled =
    !selectedAccount ||
    amountDueError ||
    transferAmount === '0.00' ||
    minimumPaymentError ||
    (selectedAccount?.source !== LendingPaymentSource.ExternalDebitCard &&
      selectedAccount?.source !== LendingPaymentSource.ExternalAch &&
      (availableLimitError || isNegativeBalance));

  const isErrorStringRed =
    minimumPaymentError ||
    amountDueError ||
    isNegativeBalance ||
    availableLimitError;

  return {
    openPlaidLink,
    amountDueFormatted,
    transferAmount,
    accountOptionsData,
    isAmountEntered,
    isSubmitDisabled,
    bottomSheetRef,
    limitErrorString,
    isErrorStringRed,
    advanceStatus,
    advanceId: activeAdvanceDetails?.id,
    handleNumPadAction,
    openBottomSheet,
    setSelectedAccount,
    selectedAccount,
    loading: isPolling || loading,
    error: advanceError || accountOptionsError,
  };
};

export default useRepayAdvanceV2;
